import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Locale resources are bundled statically (small JSON, static SPA — no async backend).
// One namespace per surface. `en` is the source of truth; `ms` holds Bahasa Malaysia.
import enCommon from "./locales/en/common.json";
import enAuth from "./locales/en/auth.json";
import enDashboard from "./locales/en/dashboard.json";
import enRegistration from "./locales/en/registration.json";
import enHome from "./locales/en/home.json";
import enSponsorship from "./locales/en/sponsorship.json";
import enRaceday from "./locales/en/raceday.json";
import enContact from "./locales/en/contact.json";
import enAccessibility from "./locales/en/accessibility.json";
import enA11y from "./locales/en/a11y.json";
import enPwd from "./locales/en/pwd.json";

import msCommon from "./locales/ms/common.json";
import msAuth from "./locales/ms/auth.json";
import msDashboard from "./locales/ms/dashboard.json";
import msRegistration from "./locales/ms/registration.json";
import msHome from "./locales/ms/home.json";
import msSponsorship from "./locales/ms/sponsorship.json";
import msRaceday from "./locales/ms/raceday.json";
import msContact from "./locales/ms/contact.json";
import msAccessibility from "./locales/ms/accessibility.json";
import msA11y from "./locales/ms/a11y.json";
import msPwd from "./locales/ms/pwd.json";

export const SUPPORTED_LANGUAGES = ["en", "ms"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const NAMESPACES = [
  "common",
  "auth",
  "dashboard",
  "registration",
  "home",
  "sponsorship",
  "raceday",
  "contact",
  "accessibility",
  "a11y",
  "pwd"
] as const;

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    registration: enRegistration,
    home: enHome,
    sponsorship: enSponsorship,
    raceday: enRaceday,
    contact: enContact,
    accessibility: enAccessibility,
    a11y: enA11y,
    pwd: enPwd
  },
  ms: {
    common: msCommon,
    auth: msAuth,
    dashboard: msDashboard,
    registration: msRegistration,
    home: msHome,
    sponsorship: msSponsorship,
    raceday: msRaceday,
    contact: msContact,
    accessibility: msAccessibility,
    a11y: msA11y,
    pwd: msPwd
  }
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    ns: NAMESPACES as unknown as string[],
    defaultNS: "common",
    // Persist + restore the choice in localStorage (mirrors the bbcklrr-a11y-prefs
    // convention used by useA11yPrefs). EN is the default until the user toggles.
    detection: {
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupLocalStorage: "bbcklrr-lang"
    },
    interpolation: { escapeValue: false }, // React already escapes
    react: { useSuspense: false }
  });

// Keep the document language in sync so screen readers and the browser announce
// content in the active language (WCAG 3.1.1 / 3.1.2).
function syncDocumentLang(lng: string) {
  document.documentElement.lang = lng.startsWith("ms") ? "ms" : "en";
}
syncDocumentLang(i18n.language || "en");
i18n.on("languageChanged", syncDocumentLang);

export default i18n;
